import { Link } from 'react-router-dom'
import { HelpCircle, ArrowLeft, Mail, Phone, MessageCircle, Clock, CheckCircle } from 'lucide-react'

const Support = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-indigo-100">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <HelpCircle className="h-8 w-8 text-indigo-600" />
                            <h1 className="text-3xl font-bold text-gray-900">Support Center</h1>
                        </div>
                        <Link
                            to="/login"
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:border-indigo-300 transition-all duration-200"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Login
                        </Link>
                    </div>
                    <p className="text-gray-600">
                        Get help with ShareVision Camera Management System
                    </p>
                </div>

                {/* Contact Methods */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-100">
                        <div className="flex items-center space-x-3 mb-4">
                            <Mail className="h-6 w-6 text-indigo-600" />
                            <h3 className="text-xl font-semibold text-gray-900">Email Support</h3>
                        </div>
                        <p className="text-gray-700 mb-3">Get help via email for technical issues and general inquiries</p>
                        <a
                            href="mailto:support@sharevision.com"
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-all duration-200"
                        >
                            <Mail className="h-4 w-4 mr-2" />
                            support@sharevision.com
                        </a>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-100">
                        <div className="flex items-center space-x-3 mb-4">
                            <MessageCircle className="h-6 w-6 text-indigo-600" />
                            <h3 className="text-xl font-semibold text-gray-900">Live Chat</h3>
                        </div>
                        <p className="text-gray-700 mb-3">Chat with our support team for immediate assistance</p>
                        <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-all duration-200">
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Start Live Chat
                        </button>
                    </div>
                </div>

                {/* Support Hours */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-indigo-100">
                    <div className="flex items-center space-x-3 mb-6">
                        <Clock className="h-6 w-6 text-indigo-600" />
                        <h2 className="text-2xl font-semibold text-gray-900">Support Hours</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-medium text-gray-900 mb-2">Business Hours</h3>
                            <p className="text-gray-700">Monday - Friday: 9:00 AM - 6:00 PM (EST)</p>
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900 mb-2">Emergency Support</h3>
                            <p className="text-gray-700">24/7 for critical security issues</p>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-indigo-100">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-6">Frequently Asked Questions</h2>

                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">How do I reset my password?</h3>
                            <p className="text-gray-700">Click on "Forgot password?" on the login page and follow the instructions sent to your email.</p>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">How do I add new cameras to the system?</h3>
                            <p className="text-gray-700">Navigate to the Cameras section in your dashboard and click "Add Camera". You'll need the RTSP URL or ISAPI credentials for your camera.</p>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">What camera types are supported?</h3>
                            <p className="text-gray-700">ShareVision supports RTSP cameras and Hikvision ISAPI cameras. Most modern IP cameras are compatible.</p>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">How do I manage user permissions?</h3>
                            <p className="text-gray-700">Administrators can manage user roles and camera access through the Users and Roles sections in the dashboard.</p>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Is my data secure?</h3>
                            <p className="text-gray-700">Yes, we use enterprise-grade security with end-to-end encryption, secure authentication, and regular security audits.</p>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Can I access the system from mobile devices?</h3>
                            <p className="text-gray-700">Yes, ShareVision includes a native mobile app for iOS and Android, plus a responsive web interface.</p>
                        </div>
                    </div>
                </div>

                {/* Technical Requirements */}
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-indigo-100">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-6">Technical Requirements</h2>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Supported Browsers</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                    Chrome 90+
                                </li>
                                <li className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                    Firefox 88+
                                </li>
                                <li className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                    Safari 14+
                                </li>
                                <li className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                    Edge 90+
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Mobile Apps</h3>
                            <ul className="space-y-2 text-gray-700">
                                <li className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                    iOS 14.0+
                                </li>
                                <li className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                    Android 8.0+
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Contact Form */}
                <div className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-6">Send us a Message</h2>

                    <form className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Enter your full name"
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                Subject
                            </label>
                            <select
                                id="subject"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">Select a subject</option>
                                <option value="technical">Technical Support</option>
                                <option value="billing">Billing Question</option>
                                <option value="feature">Feature Request</option>
                                <option value="bug">Bug Report</option>
                                <option value="general">General Inquiry</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                Message
                            </label>
                            <textarea
                                id="message"
                                rows={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Please describe your issue or question in detail..."
                            ></textarea>
                        </div>

                        <div>
                            <button
                                type="submit"
                                className="w-full md:w-auto inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg transition-all duration-200 shadow-md"
                            >
                                <Mail className="h-5 w-5 mr-2" />
                                Send Message
                            </button>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="text-center mt-8">
                    <Link
                        to="/login"
                        className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg transition-all duration-200 shadow-md"
                    >
                        <ArrowLeft className="h-5 w-5 mr-2" />
                        Return to Login
                    </Link>
                </div>
            </div>
        </div>
    )
}

export default Support